import catalog from '../catalog.json'
import { renderGuide, safeWebUrl, localImageKey } from './renderer.mjs'

export type GuideLocale = 'zh-CN' | 'en-US'
export interface GuideSummary { slug: string; category: string; title: string; summary: string }
export interface GuideGroup { id: string; title: string; articles: GuideSummary[] }
export { catalog as guideCatalog }
export { renderGuide, safeWebUrl }

const sources = import.meta.glob('../content/*/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>
const examples = import.meta.env.DEV
  ? import.meta.glob('../examples/*/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>
  : {}
const assets = import.meta.glob('../assets/**/*.{png,jpg,jpeg,webp,gif,svg}', { query: '?url&no-inline', import: 'default', eager: true }) as Record<string, string>

export const guideLocale = (locale: string): GuideLocale => locale.toLowerCase().startsWith('en') ? 'en-US' : 'zh-CN'
export function guideGroups(locale: GuideLocale): GuideGroup[] {
  return catalog.categories.map(category => ({
    id: category.id, title: category.title[locale],
    articles: catalog.articles.filter(item => item.category === category.id).sort((a, b) => a.order - b.order).map(item => ({ slug: item.slug, category: item.category, title: item.title[locale], summary: item.summary[locale] })),
  })).filter(group => group.articles.length > 0)
}

export function resolveGuideLink(href: string, base: string, includeExample = false): string | null {
  if (safeWebUrl(href) || /^#[^\s\\]*$/u.test(href)) return href
  const match = /^(?:\.\/)?([a-z0-9-]+)\.md(#[^\s\\]*)?$/.exec(href)
  if (!match || (!catalog.articles.some(item => item.slug === match[1]) && !(includeExample && match[1] === '__preview'))) return null
  return `${base}/${match[1]}${match[2] ?? ''}`
}

export function guideArticle(slug: string, locale: GuideLocale, base: string) {
  const item = catalog.articles.find(article => article.slug === slug)
  const example = import.meta.env.DEV && slug === '__preview'
  if (!item && !example) return null
  const markdown = example ? examples[`../examples/${locale}/__preview.md`] : sources[`../content/${locale}/${slug}.md`]
  if (!markdown) throw new Error(`Missing guide: ${locale}/${slug}`)
  const title = item?.title[locale] ?? (locale === 'zh-CN' ? '教程排版样例' : 'Guide layout sample')
  const summary = item?.summary[locale] ?? (locale === 'zh-CN' ? '开发预览 · 展示图片、步骤、链接和表格的写法。' : 'Development preview · Images, steps, links, and tables.')
  const rendered = renderGuide(markdown, {
    resolveLink: href => resolveGuideLink(href, base, example),
    resolveImage: href => {
      const key = localImageKey(href)
      return key ? assets[key] ?? null : null
    },
  })
  return { slug, title, summary, ...rendered }
}
